/**
 * L'onglet Activités de la fiche d'un élève : la route `student` de l'ancien espace
 * (`StudentTabsNavigator.js` → `TodoListScreen.js`, branche tuteur), sous l'en-tête de la fiche, et
 * l'écran `realizations` (« Voir plus » sous « Faites ») déplié dans l'onglet.
 *
 * Les appels du legacy, avec leurs paramètres (`TUTOR_STUDENT_ACTIVITIES_EQUIVALENCE`, un test par ligne) :
 *  - ouvrir l'onglet relit l'élève (`getStudent`, comme le montage de l'écran poussé), et la première page
 *    de ses terminées (`getStudentFinishedActivities`, que « Voir plus » lisait) : le lien les compte ;
 *  - la flèche relit l'élève (`getStudent`) ;
 *  - ranger « À faire » au doigt : `activityTodoIndexesAsTutor` (`useTutorStudentActivitiesReorder`) ;
 *  - la sélection, puis « Archiver (N) » et sa question : `deleteActivitiesTodo` ;
 *  - « Activités archivées » (le menu) pousse `archived-activites` ; « Générer une activité » ouvre la
 *    génération ; toucher une activité, à faire ou faite, ouvre sa fiche (`useTutorActivityDetails`).
 *
 * Le glisser qui révélait « Modifier le titre » et « Archiver » sur une ligne est rangé dans le menu de
 * la fiche (arbitrage A7 de l'audit). Le bouton rond « archive » à côté du « + » est retiré : la sélection
 * s'ouvre par l'appui long ou « Sélectionner ». Le prochain contrôle est dans l'Aperçu et l'onglet
 * Contrôles. Hors live, aucun message de salle ne part (`useLiveKitEventSender` n'a pas de salle).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigation } from "@react-navigation/native"
import { useDispatch, useSelector } from "react-redux"

import { apiRequest } from "../../../../../../data/redux/actions/api"
import { activitiesService } from "../../../../../../data/services/activitiesService"
import { schoolAssessmentsService } from "../../../../../../data/services/schoolAssessmentsService"
import { studentsService } from "../../../../../../data/services/studentsService"
import { ConfirmDialog, Snackbar } from "../../../../../../design-system"
import { type TutorStudentActivitiesPageProps } from "../../../../../../design-system/pages/TutorStudentActivitiesPage"
import { TutorActivityDetailsModal } from "../../../../../../design-system/tutor"
import { makeTutorStudentActivitiesViewModel } from "../../../../view-models/tutor/Students/tutorStudentActivitiesViewModel"
import { apiErrorMessage } from "../../../hooks/apiErrorMessage"
import { useBulkActivityAction, type BulkActivityWords } from "../../parent/Activities/useBulkActivityAction"
import { useRealizationsData } from "../../parent/Activities/useRealizationsData"
import { type TutorStudentTab, type TutorStudentTabArgs } from "./tutorStudentTabs"
import { useTutorActivityDetails } from "./useTutorActivityDetails"
import { useTutorStudentActivitiesReorder } from "./useTutorStudentActivitiesReorder"

/** Ce que chaque geste de l'onglet envoie, dans l'ordre. */
export const TUTOR_STUDENT_ACTIVITIES_EQUIVALENCE: { gesture: string; services: string[] }[] = [
    { gesture: "open", services: ["getStudent", "getStudentFinishedActivities"] },
    // `TodoListScreen` (tirer pour rafraîchir) : l'élève, puis ses contrôles à venir.
    { gesture: "refresh", services: ["getStudent", "getSchoolAssessments"] },
    { gesture: "reorder", services: ["activityTodoIndexesAsTutor"] },
    { gesture: "archiveSelected", services: ["deleteActivitiesTodo"] },
    { gesture: "loadMoreDone", services: ["getStudentFinishedActivities"] },
]

/**
 * Les mots de l'archivage multiple : ceux que le legacy montre au tuteur (`TEXTS.student`,
 * `TodoListScreen.js:57-59`, `:192-198`). L'accusé et l'échec, que le legacy ne disait pas, sont ceux du parent,
 * au tutoiement.
 */
export const TUTOR_ARCHIVE_WORDS: BulkActivityWords = {
    confirmTitle: (count) => `Archiver ${count} activité${count > 1 ? "s" : ""} ?`,
    confirmMessage: "Tu pourras les restaurer depuis les activités archivées.",
    doneMessage: (count) => `${count} activité${count > 1 ? "s" : ""} archivée${count > 1 ? "s" : ""}`,
    failedMessage: "Impossible d'archiver. Réessaie dans un instant.",
}

const REORDER_FAILED = {
    offline: "Pas de connexion : la liste a repris son ordre.",
    rejected: "L'ordre n'a pas été enregistré : la liste a repris le sien.",
}
const HISTORY_FAILED = "Impossible de charger ses activités terminées. Réessaie dans un instant."

type Navigation = { navigate: (name: string, params?: Record<string, unknown>) => void }

export type TutorStudentActivitiesTab = TutorStudentTab<TutorStudentActivitiesPageProps> & {
    /**
     * Ouvre la fiche d'une activité de l'élève, de n'importe quel onglet : une pièce de la conversation dont
     * une activité est née l'ouvre (`CommonChat`, branche tuteur). La fiche est montée par `overlay`.
     */
    openActivity: (activityId: string | number) => void
}

export function useTutorStudentActivitiesTab(args: TutorStudentTabArgs): TutorStudentActivitiesTab {
    const { studentId, active, openGeneration } = args
    const dispatch = useDispatch()
    const navigation = useNavigation() as unknown as Navigation

    const selectViewModel = useMemo(() => makeTutorStudentActivitiesViewModel(), [])
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const vm = useSelector((state: any) => selectViewModel(state, studentId))
    const enabled = studentId != null

    // ── Les lectures ───────────────────────────────────────────────────────────────────────────────
    const readStudent = useCallback(() => {
        if (studentId == null) return
        dispatch(apiRequest(studentsService.getStudent, { id: studentId }) as never)
    }, [dispatch, studentId])

    // La relecture de l'ancien écran (le tirer, que la flèche remplace) : l'élève et ses contrôles à venir.
    const refresh = useCallback(() => {
        if (studentId == null) return
        readStudent()
        dispatch(
            apiRequest(
                schoolAssessmentsService.getSchoolAssessments,
                {},
                {},
                {
                    student_id: studentId,
                    upcoming: true,
                }
            ) as never
        )
    }, [dispatch, readStudent, studentId])

    // Ouvrir l'onglet, c'est l'ancien écran poussé : il relisait l'élève à son montage.
    useEffect(() => {
        if (enabled && active) readStudent()
    }, [active, enabled, readStudent])

    const done = useRealizationsData(studentId, undefined, { enabled: enabled && active })
    const [historyOpen, setHistoryOpen] = useState(false)

    // ── Les gestes ─────────────────────────────────────────────────────────────────────────────────
    const details = useTutorActivityDetails({ studentId, openGeneration })
    const archive = useBulkActivityAction({
        studentId,
        activities: vm.activities,
        service: activitiesService.deleteActivitiesTodo,
        words: TUTOR_ARCHIVE_WORDS,
        gesture: "activity.archive",
    })
    const reorder = useTutorStudentActivitiesReorder(studentId)
    const { notify } = details

    const { failure: reorderFailure, dismiss: dismissReorder } = reorder
    useEffect(() => {
        if (reorderFailure == null) return
        notify(REORDER_FAILED[reorderFailure], "danger")
        // Dit une fois : le prochain échec se redira.
        dismissReorder()
    }, [dismissReorder, notify, reorderFailure])

    useEffect(() => {
        if (historyOpen && vm.historyError != null) notify(apiErrorMessage(vm.historyError, HISTORY_FAILED), "danger")
    }, [historyOpen, notify, vm.historyError])

    // Un autre élève : la sélection et le pli de ses terminées ne le suivent pas.
    // (L'identité des gestes de la sélection change avec elle : seul le changement d'élève compte.)
    const { cancelSelection } = archive.actions
    const shownStudentRef = useRef(studentId)
    useEffect(() => {
        if (shownStudentRef.current === studentId) return
        shownStudentRef.current = studentId
        cancelSelection()
        setHistoryOpen(false)
    }, [cancelSelection, studentId])

    const bulk = archive.actions
    const openActivity = details.open
    const props: TutorStudentActivitiesPageProps | undefined = enabled
        ? {
              activities: vm.activities,
              doneActivities: vm.doneToday,
              showDone: vm.showDone,
              doneHistory: {
                  count: vm.historyTotal,
                  open: historyOpen,
                  onToggle: () => setHistoryOpen((was) => !was),
                  activities: vm.history,
                  hasMore: done.hasMore,
                  loadingMore: vm.historyLoadingMore,
                  onLoadMore: done.loadMore,
              },
              onOpenActivity: (id) => openActivity(id),
              onGenerate: () => openGeneration({ kind: "student", studentId }),
              onOpenArchived: () => navigation.navigate("archived-activites", { studentId }),
              selectionMode: archive.selection.selectionMode,
              selectedIds: archive.selection.selectedIds,
              onStartSelection: bulk.startSelection,
              onToggleSelect: bulk.toggleSelect,
              onToggleSelectAll: bulk.toggleSelectAll,
              onCancelSelection: bulk.cancelSelection,
              onArchiveSelected: bulk.ask,
              archiving: archive.pending,
              onReorder: reorder.reorder,
              refreshing: vm.refreshing,
              onRefresh: refresh,
              loading: vm.loading,
              error: vm.error,
              onRetry: readStudent,
          }
        : undefined

    // Une seule fente d'accusé : celui de la fiche (ou de l'onglet, par `notify`) d'abord, puis l'archivage.
    const notice = details.notice.visible
        ? details.notice
        : {
              visible: archive.snackbar != null,
              message: archive.snackbar?.message ?? "",
              tone: archive.snackbar?.tone,
              onDismiss: bulk.dismissSnackbar,
          }

    const overlay = (
        <>
            <TutorActivityDetailsModal {...details.modal} />
            <ConfirmDialog
                visible={archive.confirm != null}
                title={archive.confirm?.title ?? ""}
                message={archive.confirm?.message}
                confirmLabel="Archiver"
                cancelLabel="Annuler"
                danger
                onConfirm={bulk.run}
                onCancel={bulk.cancelConfirm}
            />
            <Snackbar {...notice} />
        </>
    )

    return { props, overlay, openActivity }
}
